/**
 * PINs (decisions 39 and 40). Every player sets a four-digit PIN the first time a phone picks their
 * name; every admin, the owner too, sets an admin PIN the first time their admin link opens on a
 * phone. A phone that got in once is given a session and never asked again for that tournament.
 *
 * Nothing here is ever in the event log or the state every screen is sent: the Durable Object keeps
 * PINs (salted SHA-256), wrong tries and sessions in its own tables beside the pictures, and the
 * mock server and the tests keep them in memory. No screen is ever sent a hash, a salt or anyone
 * else's session.
 *
 * Wrong tries: five on one name lock that name for five minutes (a name with no wrong try for five
 * minutes starts its count again). On the hidden way into the admin side, from the public players'
 * link, twenty wrong admin PINs in five minutes across all names also pause that way in for five
 * minutes. Forty wrong player PINs in five minutes across all names pause typing a player's PIN
 * (setting a new one, and phones already in, carry on), so a script trying every name gets forty
 * guesses in five minutes, not five for each of 32 names.
 *
 * Pure apart from the clock it is handed and crypto.subtle: the Durable Object, the mock server and
 * the tests all use it.
 */

export const PIN_DIGITS = 4;
/** Wrong tries on one name before it is locked. */
export const TRIES = 5;
/** How long a locked name stays locked; also how long a run of wrong tries is remembered. */
export const LOCK_MS = 5 * 60_000;
/** Wrong admin PINs on the hidden way in, across every name, before that way in is paused. */
export const PICK_TRIES = 20;
export const PICK_WINDOW_MS = 5 * 60_000;
export const PICK_PAUSE_MS = 5 * 60_000;
/** Wrong player PINs, across every name, before typing a player's PIN is paused (same window and pause). */
export const CLAIM_TRIES = 40;
/** The owner's admin id in the PIN tables (named admins go by their own id). */
export const OWNER = "owner";

/** Whose PIN: a player's (by player id) or an admin's (by named admin id, or OWNER). */
export type PinKind = "player" | "admin";
/** Which run of wrong PINs across names: admin PINs on the hidden way in, or player PINs. */
export type Across = "admins" | "players";
/** Setting a PIN for the first time (typed twice on the phone) or typing one already set. */
export type PinMode = "set" | "enter";

/** Exactly four digits, as text. */
export function isPin(raw: unknown): raw is string {
  return typeof raw === "string" && /^[0-9]{4}$/.test(raw);
}

/** Only the digits of what was typed, at most four: what a PIN field keeps. */
export function pinDigits(raw: string): string {
  return raw.replace(/[^0-9]/g, "").slice(0, PIN_DIGITS);
}

function hex(bytes: ArrayBuffer | Uint8Array): string {
  return Array.from(bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes), (b) => b.toString(16).padStart(2, "0")).join("");
}

/** Random bytes as hex. */
export function randomHex(bytes = 16): string {
  const out = new Uint8Array(bytes);
  crypto.getRandomValues(out);
  return hex(out);
}

/** A new session: long, random, and nothing like a PIN. */
export function newSession(): string {
  return randomHex(18);
}

/** The salted SHA-256 of a PIN, as hex. The salt is the PIN's own, made when it was set. */
export async function hashPin(pin: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`bdayminton-pin:${salt}:${pin}`);
  return hex(await crypto.subtle.digest("SHA-256", data));
}

/** Two strings the same, compared in a time that does not depend on where they differ. */
export function sameText(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/** A PIN as it is kept. */
export interface PinRecord {
  hash: string;
  salt: string;
  setAt: number;
}

/** Wrong tries on one name. */
export interface Guard {
  /** Wrong tries in a row (forgotten after LOCK_MS with no wrong try, and when a lock starts). */
  streak: number;
  /** Every wrong try since the PIN was last set or reset, for the admins to see. */
  total: number;
  /** When the last wrong try was. */
  lastAt: number;
  /** Locked until then (0 or past: not locked). */
  lockedUntil: number;
}

/** Wrong PINs across every name: admin PINs on the hidden way in, or player PINs. */
export interface PickGuard {
  /** When each wrong try in the last PICK_WINDOW_MS was. */
  fails: number[];
  pausedUntil: number;
}

export const NO_PICK: PickGuard = { fails: [], pausedUntil: 0 };

/** When a name's lock ends, or null when it is not locked. */
export function lockedUntil(g: Guard | null, now: number): number | null {
  return g && g.lockedUntil > now ? g.lockedUntil : null;
}

/** One more wrong try. The fifth in a row locks the name for LOCK_MS. */
export function afterWrong(g: Guard | null, now: number): Guard {
  const was = g ?? { streak: 0, total: 0, lastAt: 0, lockedUntil: 0 };
  const streak = (now - was.lastAt > LOCK_MS ? 0 : was.streak) + 1;
  if (streak >= TRIES) return { streak: 0, total: was.total + 1, lastAt: now, lockedUntil: now + LOCK_MS };
  return { streak, total: was.total + 1, lastAt: now, lockedUntil: was.lockedUntil };
}

/** The right PIN: the run of wrong tries is over. The total stays for the admins. */
export function afterRight(g: Guard | null): Guard | null {
  return g ? { ...g, streak: 0 } : null;
}

/** When the hidden way in opens again, or null when it is open. */
export function pickPausedUntil(p: PickGuard, now: number): number | null {
  return p.pausedUntil > now ? p.pausedUntil : null;
}

/**
 * One more wrong PIN across names. The `limit`-th in the window pauses it: PICK_TRIES for admin
 * PINs on the hidden way in, CLAIM_TRIES for player PINs.
 */
export function afterPickWrong(p: PickGuard, now: number, limit = PICK_TRIES): PickGuard {
  const fails = [...p.fails.filter((t) => now - t < PICK_WINDOW_MS), now];
  if (fails.length >= limit) return { fails: [], pausedUntil: now + PICK_PAUSE_MS };
  return { fails, pausedUntil: p.pausedUntil };
}

/** One name's PIN as the admin screens see it: never the PIN, only whether there is one and the wrong tries. */
export interface PinRow {
  set: boolean;
  wrong: number;
  lastWrongAt: number | null;
  lockedUntil: number | null;
}

/** Every name's PIN as the admin screens see it. Players and admins only appear with a PIN set or a wrong try. */
export interface PinBook {
  players: Record<string, PinRow>;
  /** By named admin id, and OWNER. */
  admins: Record<string, PinRow>;
  /** The hidden way in is paused until then, or null. */
  pickPausedUntil: number | null;
  /** Typing a player's PIN is paused until then (too many wrong across names), or null. */
  claimPausedUntil: number | null;
}

export const EMPTY_BOOK: PinBook = { players: {}, admins: {}, pickPausedUntil: null, claimPausedUntil: null };

/** A row for a name with no PIN and no wrong try. */
export const NO_ROW: PinRow = { set: false, wrong: 0, lastWrongAt: null, lockedUntil: null };

export function pinRow(set: boolean, g: Guard | null, now: number): PinRow {
  return { set, wrong: g?.total ?? 0, lastWrongAt: g && g.total ? g.lastAt : null, lockedUntil: lockedUntil(g, now) };
}

/**
 * The wrong tries on a name, in words for an admin's row: "3 wrong PIN tries, 14:02", or
 * "Locked until 14:07 after 5 wrong PIN tries". Null when there has been none. `clock` turns a
 * time into "14:02" on the screen's own clock.
 */
export function triesWords(row: PinRow | undefined, now: number, clock: (ms: number) => string): string | null {
  if (!row || !row.wrong) return null;
  const tries = `${row.wrong} wrong PIN ${row.wrong === 1 ? "try" : "tries"}`;
  if (row.lockedUntil && row.lockedUntil > now) return `Locked until ${clock(row.lockedUntil)} after ${tries}`;
  return row.lastWrongAt ? `${tries}, ${clock(row.lastWrongAt)}` : tries;
}

/**
 * The same, short enough for a closed row on a phone: "Locked till 14:07", or "3 wrong PINs, 14:02".
 * Null when there has been none.
 */
export function triesShort(row: PinRow | undefined, now: number, clock: (ms: number) => string): string | null {
  if (!row || !row.wrong) return null;
  if (row.lockedUntil && row.lockedUntil > now) return `Locked till ${clock(row.lockedUntil)}`;
  const tries = `${row.wrong} wrong ${row.wrong === 1 ? "PIN" : "PINs"}`;
  return row.lastWrongAt ? `${tries}, ${clock(row.lastWrongAt)}` : tries;
}

/** Where PINs, wrong tries and sessions are kept: SQLite tables in the Durable Object, memory elsewhere. */
export interface PinStore {
  pin(kind: PinKind, id: string): PinRecord | null;
  setPin(kind: PinKind, id: string, rec: PinRecord | null): void;
  guard(kind: PinKind, id: string): Guard | null;
  setGuard(kind: PinKind, id: string, g: Guard | null): void;
  /** Wrong PINs across names: on the hidden way in ("admins", the default) or players' ("players"). */
  pick(which?: Across): PickGuard;
  setPick(p: PickGuard, which?: Across): void;
  session(token: string): { kind: PinKind; id: string } | null;
  addSession(token: string, kind: PinKind, id: string, at: number): void;
  dropSession(token: string): void;
  dropSessions(kind: PinKind, id: string): void;
  /** Every id of a kind with a PIN set, and every guard of that kind. */
  pinned(kind: PinKind): string[];
  guards(kind: PinKind): Record<string, Guard>;
  /** Forget every PIN, guard and session of a kind (a reset of the day forgets the players'). */
  wipe(kind: PinKind): void;
}

/** What a phone is told after typing a PIN. */
export type PinResult =
  | { ok: true; session: string }
  | {
      ok: false;
      reason: string;
      /** Whether this name has a PIN now, so the phone shows the right step. */
      set: boolean;
      lockedUntil: number | null;
      /** A PIN was checked and it was wrong (not a refusal before any check). */
      wrong?: boolean;
      /** This try locked the name: the admin screens are told. */
      lockedNow?: boolean;
    };

/** How a refusal reads when the name is locked. The phone puts the time in. */
export const LOCKED = "Too many wrong tries.";

/**
 * The desk every PIN goes through: setting one, typing one, the wrong tries, sessions, resets and
 * what the admin screens see.
 */
export class PinDesk {
  constructor(
    private readonly store: PinStore,
    private readonly mint: (kind: PinKind, id: string) => string = () => newSession(),
  ) {}

  isSet(kind: PinKind, id: string): boolean {
    return this.store.pin(kind, id) !== null;
  }

  lockedUntil(kind: PinKind, id: string, now: number): number | null {
    return lockedUntil(this.store.guard(kind, id), now);
  }

  /**
   * Set a PIN (mode "set", only when none is set) or type it (mode "enter"). `label` names the
   * person in the words a phone is shown. A right PIN is a new session for this phone.
   */
  async enter(kind: PinKind, id: string, pin: unknown, mode: PinMode, now: number, label: string): Promise<PinResult> {
    const refuse = (reason: string, extra: { wrong?: boolean; lockedNow?: boolean } = {}): PinResult => ({
      ok: false,
      reason,
      set: this.isSet(kind, id),
      lockedUntil: this.lockedUntil(kind, id, now),
      ...extra,
    });
    // the label starts a sentence here and sits in the middle of one there ("the owner")
    const Label = label.charAt(0).toUpperCase() + label.slice(1);
    if (!isPin(pin)) return refuse("A PIN is four numbers.");
    if (mode === "set") {
      if (this.isSet(kind, id)) return refuse(`${Label} has a PIN already. Type it, or ask an admin to reset it.`);
      const salt = randomHex(16);
      const hash = await hashPin(pin, salt);
      // two phones setting the same name at once: the first one wins, the other is told
      if (this.isSet(kind, id)) return refuse(`Someone has just set a PIN for ${label}. Type it, or ask an admin to reset it.`);
      this.store.setPin(kind, id, { hash, salt, setAt: now });
      this.store.setGuard(kind, id, null);
      return { ok: true, session: this.issue(kind, id, now) };
    }
    const rec = this.store.pin(kind, id);
    if (!rec) return refuse(`${Label} has no PIN yet. Set one.`);
    if (this.lockedUntil(kind, id, now) !== null) return refuse(LOCKED);
    const hash = await hashPin(pin, rec.salt);
    // read again after the wait: a reset or another phone's tries may have landed meanwhile
    const now2 = this.store.pin(kind, id);
    if (!now2 || now2.salt !== rec.salt) return refuse(`${Label}'s PIN has just been reset. Set a new one.`);
    if (this.lockedUntil(kind, id, now) !== null) return refuse(LOCKED);
    if (sameText(hash, rec.hash)) {
      this.store.setGuard(kind, id, afterRight(this.store.guard(kind, id)));
      return { ok: true, session: this.issue(kind, id, now) };
    }
    const g = afterWrong(this.store.guard(kind, id), now);
    this.store.setGuard(kind, id, g);
    if (g.lockedUntil > now) return refuse(LOCKED, { wrong: true, lockedNow: true });
    const left = TRIES - g.streak;
    return refuse(`That PIN is not right. ${left} more ${left === 1 ? "try" : "tries"}, then ${label} is locked for five minutes.`, { wrong: true });
  }

  /** A new session for one phone. */
  issue(kind: PinKind, id: string, now: number): string {
    const token = this.mint(kind, id);
    this.store.addSession(token, kind, id, now);
    return token;
  }

  /** Who a session is, if it is still good for that kind (and that id, when one is given). */
  check(token: unknown, kind: PinKind, id?: string): string | null {
    if (typeof token !== "string" || !token || token.length > 128) return null;
    const found = this.store.session(token);
    if (!found || found.kind !== kind) return null;
    if (id !== undefined && found.id !== id) return null;
    return found.id;
  }

  /** One phone forgets who it is ("Not you?"). */
  end(token: unknown): void {
    if (typeof token === "string" && token) this.store.dropSession(token);
  }

  /** A reset: the PIN, the wrong tries and every phone's session on that name go. */
  reset(kind: PinKind, id: string): void {
    this.store.setPin(kind, id, null);
    this.store.setGuard(kind, id, null);
    this.store.dropSessions(kind, id);
  }

  /**
   * Every PIN, wrong try and session of a kind goes (the day starting again forgets the players'),
   * and for players the pause across names too, so a reset never leaves PINs shut.
   */
  wipe(kind: PinKind): void {
    this.store.wipe(kind);
    if (kind === "player") this.store.setPick(NO_PICK, "players");
  }

  /** When the hidden way into the admin side opens again, or null. */
  pickPausedUntil(now: number): number | null {
    return pickPausedUntil(this.store.pick(), now);
  }

  /** A wrong admin PIN on the hidden way in. True when it has just paused that way in. */
  pickWrong(now: number): boolean {
    const was = this.store.pick();
    const next = afterPickWrong(was, now);
    this.store.setPick(next);
    return next.pausedUntil > now && was.pausedUntil <= now;
  }

  /** When typing a player's PIN opens again after too many wrong ones across names, or null. */
  claimPausedUntil(now: number): number | null {
    return pickPausedUntil(this.store.pick("players"), now);
  }

  /** A wrong player PIN. True when it has just paused typing players' PINs. */
  claimWrong(now: number): boolean {
    const was = this.store.pick("players");
    const next = afterPickWrong(was, now, CLAIM_TRIES);
    this.store.setPick(next, "players");
    return next.pausedUntil > now && was.pausedUntil <= now;
  }

  /** What the admin screens see. `admins` are the named admins' ids still on the list; the owner is always there. */
  book(now: number, admins: readonly string[]): PinBook {
    const rows = (kind: PinKind, only?: readonly string[]): Record<string, PinRow> => {
      const out: Record<string, PinRow> = {};
      const set = new Set(this.store.pinned(kind));
      const guards = this.store.guards(kind);
      const ids = only ?? [...new Set([...set, ...Object.keys(guards)])];
      for (const id of ids) {
        const g = guards[id] ?? null;
        if (only || set.has(id) || (g && g.total)) out[id] = pinRow(set.has(id), g, now);
      }
      return out;
    };
    return { players: rows("player"), admins: rows("admin", [OWNER, ...admins]), pickPausedUntil: this.pickPausedUntil(now), claimPausedUntil: this.claimPausedUntil(now) };
  }
}

/** A store in memory: the mock server and the tests. */
export class MemoryPinStore implements PinStore {
  protected pins = new Map<string, PinRecord>();
  protected guardsKept = new Map<string, Guard>();
  protected sessions = new Map<string, { kind: PinKind; id: string; at: number }>();
  protected pickKept: Record<Across, PickGuard> = { admins: NO_PICK, players: NO_PICK };

  private key(kind: PinKind, id: string): string {
    return `${kind}\u0000${id}`;
  }

  pin(kind: PinKind, id: string): PinRecord | null {
    return this.pins.get(this.key(kind, id)) ?? null;
  }
  setPin(kind: PinKind, id: string, rec: PinRecord | null): void {
    if (rec) this.pins.set(this.key(kind, id), rec);
    else this.pins.delete(this.key(kind, id));
  }
  guard(kind: PinKind, id: string): Guard | null {
    return this.guardsKept.get(this.key(kind, id)) ?? null;
  }
  setGuard(kind: PinKind, id: string, g: Guard | null): void {
    if (g) this.guardsKept.set(this.key(kind, id), g);
    else this.guardsKept.delete(this.key(kind, id));
  }
  pick(which: Across = "admins"): PickGuard {
    return this.pickKept[which];
  }
  setPick(p: PickGuard, which: Across = "admins"): void {
    this.pickKept = { ...this.pickKept, [which]: p };
  }
  session(token: string): { kind: PinKind; id: string } | null {
    const s = this.sessions.get(token);
    return s ? { kind: s.kind, id: s.id } : null;
  }
  addSession(token: string, kind: PinKind, id: string, at: number): void {
    this.sessions.set(token, { kind, id, at });
  }
  dropSession(token: string): void {
    this.sessions.delete(token);
  }
  dropSessions(kind: PinKind, id: string): void {
    for (const [token, s] of this.sessions) if (s.kind === kind && s.id === id) this.sessions.delete(token);
  }
  pinned(kind: PinKind): string[] {
    return [...this.pins.keys()].filter((k) => k.startsWith(`${kind}\u0000`)).map((k) => k.slice(kind.length + 1));
  }
  guards(kind: PinKind): Record<string, Guard> {
    const out: Record<string, Guard> = {};
    for (const [k, g] of this.guardsKept) if (k.startsWith(`${kind}\u0000`)) out[k.slice(kind.length + 1)] = g;
    return out;
  }
  wipe(kind: PinKind): void {
    for (const k of [...this.pins.keys()]) if (k.startsWith(`${kind}\u0000`)) this.pins.delete(k);
    for (const k of [...this.guardsKept.keys()]) if (k.startsWith(`${kind}\u0000`)) this.guardsKept.delete(k);
    for (const [token, s] of this.sessions) if (s.kind === kind) this.sessions.delete(token);
  }
}
